import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getNotifications } from '../api/api';
import { Bell, LogOut, Menu, X, Building2, ChevronDown, LayoutDashboard, ShoppingCart, Receipt, BookOpen, Wallet, Truck, BarChart3, Settings, Users, HelpCircle } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (user) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    try {
      const data = await getNotifications();
      setNotifications(data.filter(n => !n.read));
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (items) => items.some(item => isActive(item.path));

  // Menú agrupado por categorías
  const menuGroups = [
    {
      id: 'operations',
      label: 'Operaciones',
      icon: LayoutDashboard,
      roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'cashier', 'purchase_manager'],
      items: [
        { path: '/dashboard', label: 'Dashboard', roles: ['admin', 'manager', 'supervisor', 'store_manager', 'cashier', 'purchase_manager'] },
        { path: '/sales', label: 'Ventas', roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'cashier'] },
        { path: '/expenses', label: 'Gastos', roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'cashier'] },
        { path: '/cashbook', label: '📖 Libro de Caja', roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'cashier'] },
      ]
    },
    {
      id: 'cash',
      label: 'Caja',
      icon: Wallet,
      roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'cashier', 'purchase_manager'],
      items: [
        { path: '/petty-cash', label: 'Fondo Sencillo', roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'cashier'] },
        { path: '/suppliers', label: 'Proveedores', roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'purchase_manager'] },
      ]
    },
    {
      id: 'reports',
      label: 'Reportes',
      icon: BarChart3,
      roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'purchase_manager'],
      items: [
        { path: '/reports', label: 'Reportes', roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager'] },
        { path: '/annual-reports', label: 'Reportes Anuales', roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'purchase_manager'] },
        { path: '/comparative-reports', label: 'Comparativos', roles: ['admin', 'manager', 'supervisor'] },
        { path: '/pos-commissions', label: 'Comisiones POS', roles: ['admin', 'manager', 'admin_assistant'] },
        { path: '/monthly-sales', label: 'Ventas Mensuales', roles: ['admin', 'manager', 'admin_assistant'] },
        { path: '/audit', label: 'Auditoría', roles: ['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager'] },
      ]
    },
    {
      id: 'admin',
      label: 'Administración',
      icon: Settings,
      roles: ['admin'],
      items: [
        { path: '/users', label: 'Usuarios', roles: ['admin'] },
        { path: '/permissions', label: 'Permisos', roles: ['admin'] },
        { path: '/stores', label: 'Tiendas', roles: ['admin'] },
        { path: '/devices', label: 'Dispositivos', roles: ['admin'] },
        { path: '/admin-tools', label: '🔧 Herramientas', roles: ['admin'] },
        { path: '/audit-transactions', label: '🔍 Auditar Ventas', roles: ['admin'] },
      ]
    },
  ];

  // Filtrar grupos y items por rol del usuario
  const filteredGroups = menuGroups
    .filter(group => group.roles.includes(user?.role))
    .map(group => ({
      ...group,
      items: group.items.filter(item => item.roles.includes(user?.role))
    }))
    .filter(group => group.items.length > 0);

  const toggleDropdown = (groupId) => {
    setOpenDropdown(openDropdown === groupId ? null : groupId);
  };

  // Theme styles
  const navbarBg = 'bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700/50';
  const logoBg = 'bg-blue-600 group-hover:bg-blue-500';
  const activeBtn = 'bg-blue-600 text-white shadow-lg';
  const hoverBtn = 'text-slate-300 hover:text-white hover:bg-slate-700/50';
  const dropdownBg = 'bg-slate-800 border-slate-700';
  const dropdownActive = 'bg-blue-600 text-white';
  const dropdownHover = 'text-slate-300 hover:bg-slate-700 hover:text-white';

  return (
    <nav className={`${navbarBg} shadow-xl`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center space-x-3 group">
              <div className={`${logoBg} rounded-lg p-2 shadow-lg transition-all duration-300`}>
                <Building2 size={24} className="text-white" />
              </div>
              <div>
                <span className="text-white text-xl font-bold tracking-wide">
                  Siempre
                </span>
                <div className="text-slate-400 text-xs font-light tracking-wider">
                  CONTROL DE CAJA
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Dropdowns */}
          <div className="hidden lg:flex items-center space-x-1" ref={dropdownRef}>
            {filteredGroups.map(group => (
              <div key={group.id} className="relative">
                <button
                  onClick={() => toggleDropdown(group.id)}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isGroupActive(group.items)
                      ? activeBtn
                      : hoverBtn
                  }`}
                >
                  <group.icon size={16} />
                  <span>{group.label}</span>
                  <ChevronDown size={14} className={`transition-transform ${openDropdown === group.id ? 'rotate-180' : ''}`} />
                </button>
                
                {openDropdown === group.id && (
                  <div className={`absolute top-full left-0 mt-1 w-48 ${dropdownBg} rounded-lg shadow-xl border py-1 z-50`}>
                    {group.items.map(item => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setOpenDropdown(null)}
                        className={`block px-4 py-2 text-sm transition-colors ${
                          isActive(item.path)
                            ? dropdownActive
                            : dropdownHover
                        }`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {/* Ayuda - Link directo */}
            <Link
              to="/help"
              className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/help')
                  ? activeBtn
                  : hoverBtn
              }`}
            >
              <HelpCircle size={16} />
              <span>Ayuda</span>
            </Link>
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-3">
            {/* Notifications */}
            <Link 
              to="/notifications" 
              className="relative p-2.5 text-slate-300 hover:text-white transition-all duration-200 bg-slate-700/50 rounded-lg hover:bg-slate-700"
            >
              <Bell size={20} />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                  {notifications.length}
                </span>
              )}
            </Link>

            {/* User Info */}
            <div className="hidden md:flex items-center space-x-3 bg-slate-700/50 rounded-lg px-4 py-2">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">{user?.name?.charAt(0)}</span>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-slate-400">{user?.role}</p>
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={logout}
              className="p-2.5 text-slate-300 hover:text-white transition-all duration-200 bg-slate-700/50 rounded-lg hover:bg-red-600/80"
              title="Cerrar sesión"
              data-testid="logout-button"
            >
              <LogOut size={20} />
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-300 hover:text-white"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-slate-800 border-t border-slate-700 max-h-[70vh] overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-2">
            {filteredGroups.map(group => (
              <div key={group.id} className="space-y-1">
                {/* Group Header */}
                <div className="flex items-center space-x-2 px-3 py-2 text-slate-400 text-xs font-semibold uppercase tracking-wider">
                  <group.icon size={14} />
                  <span>{group.label}</span>
                </div>
                {/* Group Items */}
                {group.items.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 ml-4 rounded-lg text-sm font-medium ${
                      isActive(item.path)
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            ))}
            
            {/* Ayuda */}
            <div className="pt-2 border-t border-slate-700">
              <Link
                to="/help"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                  isActive('/help')
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <HelpCircle size={16} />
                <span>Ayuda</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
